import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CashSpecialIcon, Layout, PageTitle } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { generateDeferredPayoutLink } from "../api/deferred-payout";
import { getWidgetApiError, isDeadLinkError, type WidgetApiError } from "../services/api";

function destinationMaintenanceCopy(
  api: WidgetApiError | null,
): { title: string; body: string } | null {
  if (api?.code !== "FLOW_UNAVAILABLE_MAINTENANCE") return null;
  const details = (api.details ?? {}) as { title?: string; body?: string };
  const title = details.title?.trim() ?? "";
  const body = details.body?.trim() ?? "";
  if (!title && !body) return null;
  return { title, body };
}
import { getEvents, safeCapture } from "../analytics";
import {
  clearFirstSendWarningCache,
  wasFirstSendWarningShown,
} from "../hooks/useFirstSendWarning";
import { clearPhoneSkip } from "../utils/phoneGate";

const MIN_MINTING_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_MINTING_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/** Mints the payout-claim link after CIP, then routes to the share screen. */
export const MintingPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { draft, setMintResult, setComposeDraft, setPendingRecipientName } =
    useRequestStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  const mintingRef = useRef(false);
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    if (mintingRef.current) return;

    const linkId = linkIdFromRoute || session.data?.linkId;
    const userStatus = session.data?.userStatus;
    const canOriginate = !userStatus?.blocked && !userStatus?.suspended;
    if (!canOriginate) {
      redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
      return;
    }
    if (!draft || !linkId) {
      redirectTo(APP_ROUTES.TRANSFER_PAGE);
      return;
    }

    mintingRef.current = true;

    generateDeferredPayoutLink(linkId, {
      sourceCountry: draft.sourceCountry,
      destinationCountry: draft.destinationCountry,
      sourceCurrency: draft.sourceCurrency,
      destinationCurrency: draft.destinationCurrency,
      sendAmount: draft.sendAmount,
      totalDebit: draft.totalDebit,
      fundingMethod: draft.fundingMethod,
      recipientName: draft.recipientName,
      ...(draft.fundingPaymentMethodId
        ? { fundingPaymentMethodId: draft.fundingPaymentMethodId }
        : {}),
    })
      .then(async (result) => {
        safeCapture(getEvents().REQUEST_CREATED, {
          success: true,
          first_send_warning_shown: wasFirstSendWarningShown(),
          origination_id: result.originationId,
          link_id: result.linkId,
          expires_at: result.expiresAt,
          send_amount: draft.sendAmount,
          funding_method: draft.fundingMethod,
          funding_payment_method_id: draft.fundingPaymentMethodId,
          from_country: draft.sourceCountry,
          to_country: draft.destinationCountry,
        });
        clearFirstSendWarningCache();
        if (linkId) clearPhoneSkip(linkId);
        setMintResult(result);
        setComposeDraft(null);
        setPendingRecipientName("");
        await holdMinimumVisible(visibleSinceRef.current);
        if (draft.fundingMethod === "CASH_DEPOSIT") {
          redirectTo(APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE, { replace: true });
          return;
        }
        redirectTo(APP_ROUTES.REQUEST_READY_PAGE, { replace: true });
      })
      .catch((err) => {
        safeCapture(getEvents().REQUEST_CREATED, {
          success: false,
          first_send_warning_shown: wasFirstSendWarningShown(),
          error: err instanceof Error ? err.message : "Mint failed",
          error_code: "REQUEST_CREATE_FAILED",
        });
        clearFirstSendWarningCache();
        if (linkId) clearPhoneSkip(linkId);

        // Link expired / revoked while minting: the dead-link handler already
        // routed to the screen that explains why the link stopped working.
        if (isDeadLinkError(err)) {
          return;
        }

        const api = getWidgetApiError(err);
        const maintenance = destinationMaintenanceCopy(api);
        if (maintenance) {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: maintenance.title,
              subTitle: null,
              message: maintenance.body,
              btnText: t("common.back"),
              isOut: false,
              pageUrl: APP_ROUTES.TRANSFER_PAGE,
            },
          });
          return;
        }

        const apiCode = api?.code;
        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.transfersDisabledTitle"),
              subTitle: null,
              message: t("error.transfersDisabledMessage"),
              btnText: t("common.needHelp"),
              isOut: false,
              pageUrl: APP_ROUTES.SUPPORT_PAGE,
            },
          });
          return;
        }

        redirectTo(APP_ROUTES.ERROR_PAGE);
      });
  }, [
    draft,
    session.data?.linkId,
    linkIdFromRoute,
    setMintResult,
    setComposeDraft,
    setPendingRecipientName,
    redirectTo,
    t,
  ]);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("minting.title")}
              highlightColor={TITLE_COLOR}
              secudnary={t("minting.subtitle")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
